import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import { OKR, KeyResult, Departamento, OKRStatus } from '@/types';
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
  CollisionDetection,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertCircle, Clock, CheckCircle2, Target, TrendingUp, XCircle, Ban, Calendar, History, Paperclip, Edit, Plus,
  ChevronDown, ChevronUp, Trash2, PauseCircle, GripVertical, ArrowUp, ArrowDown, CheckCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getProgressColorClass, getProgressIndicatorStyle, getDeadlineStatus, getDeadlineBadgeStyle } from '@/lib/progress-utils';
import { toast } from 'sonner';
import { formatDateOnly } from '@/lib/dateOnly';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export type KanbanColumn = {
  id: OKRStatus;
  title: string;
  icon: React.ElementType;
  className: string;
  bgClassName: string;
};

export const columns: KanbanColumn[] = [
  {
    id: 'a_fazer',
    title: 'A Fazer',
    icon: AlertCircle,
    className: 'status-todo',
    bgClassName: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800/50',
  },
  {
    id: 'fazendo',
    title: 'Fazendo',
    icon: Clock,
    className: 'status-doing',
    bgClassName: 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800/50',
  },
  {
    id: 'pausado',
    title: 'Pausado',
    icon: PauseCircle,
    className: 'status-paused',
    bgClassName: 'bg-slate-50 border-slate-200 dark:bg-slate-800/30 dark:border-slate-700/50',
  },
  {
    id: 'feito',
    title: 'Feito',
    icon: CheckCircle2,
    className: 'status-done',
    bgClassName: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800/50',
  },
];

// "Cancelado" é uma coluna à parte de `columns` (tem card/regra própria — CancelledOKRCard,
// sem drag de OKR entre colunas), mas no desktop ela também vira uma coluna do grid, então
// participa da mesma ordenação e do mesmo controle de mostrar/ocultar que as demais.
const CANCELLED_COLUMN: KanbanColumn = {
  id: 'cancelado',
  title: 'Cancelado',
  icon: XCircle,
  className: '',
  bgClassName: 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800/50',
};

const ALL_COLUMN_IDS: OKRStatus[] = [...columns.map((c) => c.id), CANCELLED_COLUMN.id];
const columnsById = new Map<OKRStatus, KanbanColumn>([...columns, CANCELLED_COLUMN].map((c) => [c.id, c]));

// Mesma lógica de prioridade/aprovação usada na visão de lista (OKRListView), para que o
// Kanban mostre os Key Results de forma idêntica.
function getKRPriorityLabel(index: number) {
  if (index === 0) return { label: 'Alta', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
  if (index === 1) return { label: 'Média', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' };
  if (index === 2) return { label: 'Normal', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
  return null;
}

function getKRApprovalStatusBadge(kr: KeyResult) {
  if (!kr.aprovacao) return null;
  switch (kr.aprovacao.status) {
    case 'pending':
      return (
        <Badge variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
          <Clock className="h-3 w-3" />
          Aguardando aprovação
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 gap-1" title={kr.aprovacao.motivo_rejeicao}>
          <XCircle className="h-3 w-3" />
          Rejeitado
        </Badge>
      );
    case 'approved':
      return (
        <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 gap-1">
          <CheckCircle className="h-3 w-3" />
          Aprovado
        </Badge>
      );
    default:
      return null;
  }
}

export const HIDDEN_COLUMNS_STORAGE_KEY = 'okr_kanban_colunas_ocultas';

export function loadHiddenColumns(): OKRStatus[] {
  try {
    const raw = localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => ALL_COLUMN_IDS.includes(id)) : [];
  } catch {
    return [];
  }
}

const COLUMN_ORDER_STORAGE_KEY = 'okr_kanban_ordem_colunas';

function loadColumnOrder(): OKRStatus[] {
  try {
    const raw = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    if (!raw) return ALL_COLUMN_IDS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return ALL_COLUMN_IDS;
    const valid = parsed.filter((id): id is OKRStatus => ALL_COLUMN_IDS.includes(id));
    const faltando = ALL_COLUMN_IDS.filter((id) => !valid.includes(id));
    return [...valid, ...faltando];
  } catch {
    return ALL_COLUMN_IDS;
  }
}

const columnDragId = (id: OKRStatus) => `column-${id}`;
const columnIdFromDragId = (dragId: string) => dragId.replace(/^column-/, '') as OKRStatus;

function getClientPoint(event: Event): { x: number; y: number } {
  if (event instanceof MouseEvent) return { x: event.clientX, y: event.clientY };
  if (event instanceof TouchEvent) {
    const touch = event.touches[0] ?? event.changedTouches[0];
    return { x: touch?.clientX ?? 0, y: touch?.clientY ?? 0 };
  }
  return { x: 0, y: 0 };
}

// O corpo de cada coluna (onde ficam os cards de OKR) fica sobreposto, na tela, à própria
// área "arrastável" da coluna — sem filtrar por tipo, o closestCorners às vezes escolhe um
// card por baixo em vez da coluna. Ao arrastar uma coluna, só considera outras colunas como
// alvo; ao arrastar um card, ignora as colunas e volta ao comportamento padrão.
const collisionDetectionStrategy: CollisionDetection = (args) => {
  const isColumnDrag = String(args.active.id).startsWith('column-');
  const filteredArgs = {
    ...args,
    droppableContainers: args.droppableContainers.filter((container) =>
      isColumnDrag ? String(container.id).startsWith('column-') : !String(container.id).startsWith('column-')
    ),
  };
  return closestCorners(filteredArgs);
};

function SortableColumn({
  column,
  count,
  badgeClassName,
  children,
}: {
  column: KanbanColumn;
  count: number;
  badgeClassName?: string;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: columnDragId(column.id),
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="space-y-3" data-column-drag-id={columnDragId(column.id)}>
      <div className={cn('flex items-center gap-2 p-3 rounded-lg border', column.bgClassName)}>
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none -ml-1 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10 text-muted-foreground flex-shrink-0"
          title="Arrastar para reordenar coluna"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <column.icon className="h-4 w-4" />
        <span className="font-medium text-sm">{column.title}</span>
        <Badge variant="secondary" className={cn('ml-auto', badgeClassName)}>{count}</Badge>
      </div>
      {children}
    </div>
  );
}

interface OKRCardProps {
  okr: OKR;
  keyResults: KeyResult[];
  departamento?: Departamento;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onUpdateKR: (kr: KeyResult, progresso: number, anexoFile?: File | null) => void;
  onCancelar: () => void;
  onOpenHistorico: () => void;
  onEditOKR: () => void;
  onAddKR: () => void;
  onDeleteOKR: () => void;
  onEditKR: (kr: KeyResult) => void;
  onDeleteKR: (krId: string) => void;
  onReorderKR: (krId: string, direction: 'up' | 'down') => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  canCancelar: boolean;
  canEditar: boolean;
  isDragging?: boolean;
  allExpanded?: boolean;
}

function DroppableColumn({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-[400px] p-3 rounded-lg bg-muted/30 border border-dashed transition-colors',
        isOver ? 'border-primary bg-primary/5' : 'border-border'
      )}
      data-status={id}
    >
      {children}
    </div>
  );
}

function DraggableOKRCard({
  okr,
  keyResults,
  departamento,
  onMoveLeft,
  onMoveRight,
  onUpdateKR,
  onCancelar,
  onOpenHistorico,
  onEditOKR,
  onAddKR,
  onDeleteOKR,
  onEditKR,
  onDeleteKR,
  onReorderKR,
  canMoveLeft,
  canMoveRight,
  canCancelar,
  canEditar,
  allExpanded,
}: OKRCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: okr.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none">
      <OKRCard
        okr={okr}
        keyResults={keyResults}
        departamento={departamento}
        onMoveLeft={onMoveLeft}
        onMoveRight={onMoveRight}
        onUpdateKR={onUpdateKR}
        onCancelar={onCancelar}
        onOpenHistorico={onOpenHistorico}
        onEditOKR={onEditOKR}
        onAddKR={onAddKR}
        onDeleteOKR={onDeleteOKR}
        onEditKR={onEditKR}
        onDeleteKR={onDeleteKR}
        onReorderKR={onReorderKR}
        canMoveLeft={canMoveLeft}
        canMoveRight={canMoveRight}
        canCancelar={canCancelar}
        canEditar={canEditar}
        isDragging={isDragging}
        allExpanded={allExpanded}
      />
    </div>
  );
}

function OKRCard({
  okr,
  keyResults,
  departamento,
  onMoveLeft,
  onMoveRight,
  onUpdateKR,
  onCancelar,
  onOpenHistorico,
  onEditOKR,
  onAddKR,
  onDeleteOKR,
  onEditKR,
  onDeleteKR,
  onReorderKR,
  canMoveLeft,
  canMoveRight,
  canCancelar,
  canEditar,
  isDragging = false,
  allExpanded,
}: OKRCardProps) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Botão global "ver/ocultar todos os Key Results" na toolbar — sincroniza todos os
  // cards com o toggle geral, sem impedir que o usuário ainda expanda/recolha um card
  // individual depois (o efeito só reage quando o valor global muda).
  useEffect(() => {
    if (allExpanded !== undefined) setIsExpanded(allExpanded);
  }, [allExpanded]);
  const [editingKR, setEditingKR] = useState<string | null>(null);
  const [newProgress, setNewProgress] = useState('');
  const [completionFiles, setCompletionFiles] = useState<Record<string, File | null>>({});
  const newProgressJustFocused = useRef(false);

  const isComum = user?.tipo === 'comum';
  const progressColor = getProgressColorClass(okr.progresso_geral);

  const handleSaveProgress = () => {
    if (editingKR && newProgress) {
      const currentKR = keyResults.find((kr) => kr.id === editingKR);
      if (!currentKR) return;
      const selectedFile = completionFiles[editingKR] || null;
      const progressValue = Number(newProgress);
      if (currentKR.anexo_obrigatorio && progressValue >= currentKR.progresso_meta && !currentKR.anexo_url && !selectedFile) {
        toast.error('Este Key Result exige um anexo para ser concluído. Selecione um arquivo antes de salvar.');
        return;
      }
      onUpdateKR(currentKR, progressValue, selectedFile);
      setCompletionFiles((prev) => ({ ...prev, [editingKR]: null }));
      setEditingKR(null);
      setNewProgress('');
    }
  };

  return (
    <>
      <Card
        className={cn('card-interactive mb-3 bg-card transition-opacity', isDragging ? 'opacity-50' : 'hover:shadow-md')}
        onClick={() => setIsOpen(true)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-3">
            <h3 className="font-medium text-sm leading-tight line-clamp-2">{okr.titulo}</h3>
            <TooltipProvider delayDuration={500}>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); setIsExpanded((prev) => !prev); }}
                    >
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isExpanded ? 'Ocultar Key Results' : 'Ver Key Results'}</TooltipContent>
                </Tooltip>
                {canEditar && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); onAddKR(); }}
                      >
                        <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Adicionar Key Result</TooltipContent>
                  </Tooltip>
                )}
                {canEditar && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); onEditOKR(); }}
                      >
                        <Edit className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar OKR</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); onOpenHistorico(); }}
                    >
                      <History className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Ver histórico</TooltipContent>
                </Tooltip>
                {canEditar && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); onDeleteOKR(); }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Excluir OKR</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </TooltipProvider>
          </div>

          {departamento && (
            <Badge variant="secondary" className="text-xs mb-2">{departamento.nome}</Badge>
          )}

          {(okr.data_inicio || okr.data_fim) && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
              {okr.data_inicio && (
                <Badge variant="outline" className="text-[10px] gap-1 font-normal px-1.5 py-0.5">
                  <Calendar className="h-2.5 w-2.5" />
                  {formatDateOnly(okr.data_inicio)}
                </Badge>
              )}
              {okr.data_fim && (() => {
                const deadlineStatus = getDeadlineStatus(okr.data_fim);
                const deadlineStyle = getDeadlineBadgeStyle(deadlineStatus);
                return (
                  <Badge variant="outline" className={cn('text-[10px] gap-1 font-normal px-1.5 py-0.5', deadlineStyle)}>
                    <Clock className="h-2.5 w-2.5" />
                    {formatDateOnly(okr.data_fim)}
                    {deadlineStatus === 'overdue' && ' !'}
                    {deadlineStatus === 'warning' && ' •'}
                  </Badge>
                );
              })()}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{keyResults.length} Key Results</span>
              <span className="font-medium">{okr.progresso_geral}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all duration-500', progressColor)}
                style={{ width: `${okr.progresso_geral}%` }}
              />
            </div>
          </div>

          {isExpanded && (
            <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
              {keyResults.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhum Key Result cadastrado.</p>
              ) : (
                keyResults.map((kr, index) => {
                  const priority = getKRPriorityLabel(index);
                  const approvalBadge = getKRApprovalStatusBadge(kr);
                  const deadlineStatus = kr.prazo ? getDeadlineStatus(kr.prazo) : null;
                  const deadlineStyle = deadlineStatus ? getDeadlineBadgeStyle(deadlineStatus) : '';
                  return (
                    <div key={kr.id} className="p-2 rounded-md bg-muted/50 space-y-1.5 text-xs">
                      <div className="flex items-center gap-1 flex-wrap">
                        {priority && (
                          <Badge className={cn('text-[10px] px-1.5 py-0', priority.className)}>{priority.label}</Badge>
                        )}
                        {kr.prazo && (
                          <Badge variant="outline" className={cn('text-[10px] gap-1 font-normal px-1.5 py-0.5', deadlineStyle)}>
                            <Calendar className="h-2.5 w-2.5" />
                            {formatDateOnly(kr.prazo)}
                            {deadlineStatus === 'overdue' && ' !'}
                            {deadlineStatus === 'warning' && ' •'}
                          </Badge>
                        )}
                        {kr.anexo_obrigatorio && (
                          <Badge variant="outline" className="text-[10px] gap-1 font-normal px-1.5 py-0.5 text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400">
                            <Paperclip className="h-2.5 w-2.5" />
                            Anexo
                          </Badge>
                        )}
                        {approvalBadge}
                      </div>

                      <p className="truncate">{kr.descricao}</p>

                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-muted-foreground flex-shrink-0">
                          {kr.progresso_atual} / {kr.progresso_meta} ({kr.percentual}%)
                        </span>
                        {canEditar && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); onReorderKR(kr.id, 'up'); }}
                              disabled={index === 0}
                              title="Mover para cima"
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); onReorderKR(kr.id, 'down'); }}
                              disabled={index === keyResults.length - 1}
                              title="Mover para baixo"
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); onEditKR(kr); }}
                              title="Editar Key Result"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={(e) => { e.stopPropagation(); onDeleteKR(kr.id); }}
                              title="Excluir Key Result"
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          <div className="flex justify-between mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onMoveLeft(); }}
              disabled={!canMoveLeft}
              className="h-7 px-2 text-xs"
            >
              ← Anterior
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onMoveRight(); }}
              disabled={!canMoveRight}
              className="h-7 px-2 text-xs"
            >
              Próximo →
            </Button>
          </div>

          {canCancelar && (
            <div className="mt-2 pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); onCancelar(); }}
                className="w-full h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Ban className="h-3 w-3 mr-1" />
                Cancelar OKR
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              {okr.titulo}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{okr.descricao}</p>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <span>Progresso: <strong>{okr.progresso_geral}%</strong></span>
              </div>
              {departamento && <Badge variant="secondary">{departamento.nome}</Badge>}
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm">Key Results</h4>
              <ScrollArea barraQuandoTransborda rotuloRolagem="Key Results" className="max-h-64">
                <div className="space-y-3">
                  {keyResults.map((kr) => (
                    <div key={kr.id} className="p-3 rounded-lg bg-muted/50 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm flex-1">{kr.descricao}</p>
                        <span className="text-sm font-medium text-muted-foreground">{kr.percentual}%</span>
                      </div>

                      <Progress value={kr.percentual} className={cn('h-1.5', getProgressIndicatorStyle(kr.percentual))} />

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{kr.progresso_atual} / {kr.progresso_meta}</span>

                        {editingKR === kr.id ? (
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={newProgress}
                                onChange={(e) => setNewProgress(e.target.value)}
                                onFocus={(e) => { newProgressJustFocused.current = true; e.target.select(); }}
                                onMouseUp={(e) => { if (newProgressJustFocused.current) { e.preventDefault(); newProgressJustFocused.current = false; } }}
                                className="h-7 w-20 text-xs"
                                placeholder="Valor"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleSaveProgress(); }} className="h-7 text-xs">
                                Salvar
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingKR(null);
                                  setCompletionFiles((prev) => ({ ...prev, [kr.id]: null }));
                                }}
                                className="h-7 text-xs"
                              >
                                Cancelar
                              </Button>
                            </div>
                            {(kr.anexo_obrigatorio || (isComum && kr.aprovar_alteracoes)) && (
                              <div className="flex flex-col items-end gap-1">
                                <Input
                                  type="file"
                                  accept=".pdf,.csv,.png,.jpg,.jpeg,.gif,.webp"
                                  className="h-7 text-[10px] w-44"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    setCompletionFiles((prev) => ({
                                      ...prev,
                                      [kr.id]: e.target.files?.[0] || null,
                                    }));
                                  }}
                                />
                                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                  <Paperclip className="h-3 w-3" />
                                  {kr.anexo_obrigatorio ? 'Anexo obrigatório para conclusão' : 'Anexo opcional'}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingKR(kr.id);
                              setNewProgress(String(kr.progresso_atual));
                              setCompletionFiles((prev) => ({ ...prev, [kr.id]: null }));
                            }}
                            className="h-6 text-xs border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                          >
                            Atualizar progresso
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}

                  {keyResults.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">Nenhum Key Result cadastrado</p>
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CancelledOKRCard({ okr, departamento }: { okr: OKR; departamento?: Departamento }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Card
        className="mb-3 bg-card border-red-200 opacity-75 cursor-pointer hover:opacity-100 transition-opacity"
        onClick={() => setIsOpen(true)}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-medium text-sm leading-tight line-clamp-2 text-muted-foreground line-through">{okr.titulo}</h3>
            <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
          </div>
          {departamento && <Badge variant="secondary" className="text-xs mb-2">{departamento.nome}</Badge>}
          <p className="text-xs text-muted-foreground line-clamp-2">{okr.motivo_cancelamento || 'Motivo não informado'}</p>
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              OKR Cancelada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-sm mb-1">{okr.titulo}</h4>
              <p className="text-sm text-muted-foreground">{okr.descricao}</p>
            </div>
            {departamento && <Badge variant="secondary">{departamento.nome}</Badge>}
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 space-y-3">
              <h5 className="font-medium text-sm text-destructive">Motivo do Cancelamento</h5>
              <p className="text-sm">{okr.motivo_cancelamento || 'Motivo não informado'}</p>
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2 border-t border-destructive/20">
                {okr.data_cancelamento && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDateOnly(okr.data_cancelamento)}</span>
                  </div>
                )}
                {okr.cancelado_por && (
                  <div className="flex items-center gap-1">
                    <span>{okr.cancelado_por}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export interface OKRBoardViewProps {
  okrs: OKR[];
  keyResults: KeyResult[];
  departamentos: Departamento[];
  isAdmin: boolean;
  onMoveOKR: (okrId: string, newStatus: OKRStatus) => void;
  onUpdateKR: (kr: KeyResult, progresso: number, anexoFile?: File | null) => void;
  onOpenCancelDialog: (okr: OKR) => void;
  onOpenHistorico: (okr: OKR) => void;
  onEditOKR: (okr: OKR) => void;
  onAddKR: (okrId: string) => void;
  onDeleteOKR: (okrId: string) => void;
  onEditKR: (kr: KeyResult, okrId: string) => void;
  onDeleteKR: (krId: string) => void;
  onReorderKR: (okrId: string, krId: string, direction: 'up' | 'down') => void;
  hiddenColumns: OKRStatus[];
  allKRsExpanded?: boolean;
}

export function OKRBoardView({
  okrs,
  keyResults,
  departamentos,
  isAdmin,
  onMoveOKR,
  onUpdateKR,
  onOpenCancelDialog,
  onOpenHistorico,
  onEditOKR,
  onAddKR,
  onDeleteOKR,
  onEditKR,
  onDeleteKR,
  onReorderKR,
  hiddenColumns,
  allKRsExpanded,
}: OKRBoardViewProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeColumnWidth, setActiveColumnWidth] = useState<number | null>(null);
  // Posição do preview da coluna arrastada, calculada a partir do ponteiro real (não da
  // medição de rect do dnd-kit — ver comentário em handleDragStart/handleDragMove).
  const [columnPreviewPos, setColumnPreviewPos] = useState<{ x: number; y: number } | null>(null);
  const columnDragOriginRef = useRef<{ x: number; y: number } | null>(null);
  const [columnOrder, setColumnOrder] = useState<OKRStatus[]>(() => loadColumnOrder());
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(columnOrder));
  }, [columnOrder]);

  const visibleColumns = columns.filter((c) => !hiddenColumns.includes(c.id));

  // Ordem+visibilidade para o grid desktop (inclui a coluna "Cancelado", que também é
  // arrastável ali); o Kanban mobile (abas) continua na ordem fixa de `visibleColumns`.
  const orderedDesktopColumns = columnOrder
    .filter((id) => !hiddenColumns.includes(id))
    .map((id) => columnsById.get(id))
    .filter((c): c is KanbanColumn => !!c);
  const orderedStatusColumns = orderedDesktopColumns.filter((c) => c.id !== 'cancelado');

  const getKRsForOKR = (okrId: string) => keyResults.filter((kr) => kr.okr_id === okrId);
  const getDepartamento = (depId: string) => departamentos.find((d) => d.id === depId);
  const getOKRsByStatus = (status: OKRStatus) => okrs.filter((o) => o.status === status);
  const cancelledOkrs = okrs.filter((o) => o.status === 'cancelado');

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    const activeIdStr = String(event.active.id);
    if (activeIdStr.startsWith('column-')) {
      // O preview no DragOverlay tinha largura fixa (280px), diferente da largura real da
      // coluna — em telas com mais colunas visíveis (mais estreitas), o preview ficava mais
      // largo que a coluna e "vazava" para a vizinha, parecendo torto/desalinhado ao arrastar.
      // event.active.rect.current.initial ainda é null neste ponto (a medição do dnd-kit só
      // é preenchida depois do onDragStart), então lemos a largura real direto do DOM.
      const el = document.querySelector(`[data-column-drag-id="${activeIdStr}"]`);
      setActiveColumnWidth(el ? el.getBoundingClientRect().width : null);

      // O preview de coluna usava o DragOverlay padrão do dnd-kit, que posiciona a partir
      // do rect medido do nó de origem (a coluna inteira, com todos os cards abaixo do
      // cabeçalho). Como esse nó é bem maior que o preview, e ainda recebe seu próprio
      // transform de reordenação durante o drag, a posição calculada ficava desalinhada e
      // "torta" em relação ao cursor. Em vez de confiar nessa medição, seguimos o ponteiro
      // diretamente: guardamos a posição inicial do clique e, a cada movimento, somamos o
      // delta reportado pelo dnd-kit — o preview fica sempre exatamente sob o mouse.
      const origin = getClientPoint(event.activatorEvent);
      columnDragOriginRef.current = origin;
      setColumnPreviewPos(origin);
    }
  };

  const handleDragMove = (event: DragMoveEvent) => {
    if (!columnDragOriginRef.current) return;
    setColumnPreviewPos({
      x: columnDragOriginRef.current.x + event.delta.x,
      y: columnDragOriginRef.current.y + event.delta.y,
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveColumnWidth(null);
    columnDragOriginRef.current = null;
    setColumnPreviewPos(null);
    if (!over) return;

    const activeIdStr = String(active.id);

    if (activeIdStr.startsWith('column-')) {
      const overIdStr = String(over.id);
      if (!overIdStr.startsWith('column-') || activeIdStr === overIdStr) return;
      const activeColId = columnIdFromDragId(activeIdStr);
      const overColId = columnIdFromDragId(overIdStr);
      setColumnOrder((prev) => {
        const oldIndex = prev.indexOf(activeColId);
        const newIndex = prev.indexOf(overColId);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
      return;
    }

    const okrId = activeIdStr;
    const okr = okrs.find((o) => o.id === okrId);
    if (!okr) return;

    let newStatus: OKRStatus | null = null;
    if (columns.some((col) => col.id === over.id)) {
      newStatus = over.id as OKRStatus;
    } else {
      const overOkr = okrs.find((o) => o.id === over.id);
      if (overOkr) {
        newStatus = overOkr.status;
      }
    }

    if (!newStatus || okr.status === newStatus) return;
    onMoveOKR(okrId, newStatus);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveColumnWidth(null);
    columnDragOriginRef.current = null;
    setColumnPreviewPos(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionStrategy}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {visibleColumns.length === 0 && orderedDesktopColumns.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-sm text-muted-foreground border border-dashed rounded-lg">
          Todas as colunas estão ocultas — use o filtro "Colunas" acima para reexibir.
        </div>
      ) : (
        <>
      {/* Mobile: tabs por coluna */}
      {visibleColumns.length > 0 && (
      <div className="md:hidden">
        <Tabs defaultValue={visibleColumns[0].id}>
          <TabsList className="w-full">
            {visibleColumns.map((column) => {
              const count = getOKRsByStatus(column.id).length;
              return (
                <TabsTrigger key={column.id} value={column.id} className="flex-1 gap-1.5">
                  <column.icon className="h-3.5 w-3.5" />
                  {column.title}
                  {count > 0 && <Badge variant="secondary" className="h-4 px-1 text-[10px]">{count}</Badge>}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {visibleColumns.map((column, columnIndex) => {
            const columnOkrs = getOKRsByStatus(column.id);
            const okrIds = columnOkrs.map((okr) => okr.id);
            return (
              <TabsContent key={column.id} value={column.id} className="mt-3">
                <DroppableColumn id={column.id}>
                  <SortableContext id={column.id} items={okrIds} strategy={verticalListSortingStrategy}>
                    {columnOkrs.map((okr) => (
                      <DraggableOKRCard
                        key={okr.id}
                        okr={okr}
                        keyResults={getKRsForOKR(okr.id)}
                        departamento={getDepartamento(okr.departamento_id)}
                        onMoveLeft={() => { if (columnIndex > 0) onMoveOKR(okr.id, visibleColumns[columnIndex - 1].id); }}
                        onMoveRight={() => { if (columnIndex < visibleColumns.length - 1) onMoveOKR(okr.id, visibleColumns[columnIndex + 1].id); }}
                        onUpdateKR={onUpdateKR}
                        onCancelar={() => onOpenCancelDialog(okr)}
                        onOpenHistorico={() => onOpenHistorico(okr)}
                        onEditOKR={() => onEditOKR(okr)}
                        onAddKR={() => onAddKR(okr.id)}
                        onDeleteOKR={() => onDeleteOKR(okr.id)}
                        onEditKR={(kr) => onEditKR(kr, okr.id)}
                        onDeleteKR={onDeleteKR}
                        onReorderKR={(krId, direction) => onReorderKR(okr.id, krId, direction)}
                        canMoveLeft={columnIndex > 0}
                        canMoveRight={columnIndex < visibleColumns.length - 1}
                        canCancelar={isAdmin}
                        canEditar={isAdmin}
                        allExpanded={allKRsExpanded}
                      />
                    ))}
                    {columnOkrs.length === 0 && (
                      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        Nenhuma OKR neste status
                      </div>
                    )}
                  </SortableContext>
                </DroppableColumn>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>
      )}

      {/* Desktop: grid de colunas — arrastando o "grip" do cabeçalho, reordena as colunas entre si */}
      {orderedDesktopColumns.length > 0 && (
      <SortableContext
        items={orderedDesktopColumns.map((c) => columnDragId(c.id))}
        strategy={horizontalListSortingStrategy}
      >
        <div
          className={cn(
            'hidden md:grid gap-6',
            {
              1: 'md:grid-cols-1',
              2: 'md:grid-cols-2',
              3: 'md:grid-cols-3',
              4: 'md:grid-cols-4',
              5: 'md:grid-cols-5',
            }[orderedDesktopColumns.length] ?? 'md:grid-cols-4'
          )}
        >
          {orderedDesktopColumns.map((column) => {
            if (column.id === 'cancelado') {
              return (
                <SortableColumn key={column.id} column={column} count={cancelledOkrs.length} badgeClassName="bg-red-100 text-red-800">
                  <div className="min-h-[400px] p-3 rounded-lg bg-muted/30 border border-dashed border-border">
                    {cancelledOkrs.length === 0 ? (
                      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        Nenhuma OKR cancelada
                      </div>
                    ) : (
                      cancelledOkrs.map((okr) => (
                        <CancelledOKRCard key={okr.id} okr={okr} departamento={getDepartamento(okr.departamento_id)} />
                      ))
                    )}
                  </div>
                </SortableColumn>
              );
            }

            const columnOkrs = getOKRsByStatus(column.id);
            const okrIds = columnOkrs.map((okr) => okr.id);
            const statusIdx = orderedStatusColumns.findIndex((c) => c.id === column.id);
            return (
              <SortableColumn key={column.id} column={column} count={columnOkrs.length}>
                <DroppableColumn id={column.id}>
                  <SortableContext id={column.id} items={okrIds} strategy={verticalListSortingStrategy}>
                    {columnOkrs.map((okr) => (
                      <DraggableOKRCard
                        key={okr.id}
                        okr={okr}
                        keyResults={getKRsForOKR(okr.id)}
                        departamento={getDepartamento(okr.departamento_id)}
                        onMoveLeft={() => { if (statusIdx > 0) onMoveOKR(okr.id, orderedStatusColumns[statusIdx - 1].id); }}
                        onMoveRight={() => { if (statusIdx < orderedStatusColumns.length - 1) onMoveOKR(okr.id, orderedStatusColumns[statusIdx + 1].id); }}
                        onUpdateKR={onUpdateKR}
                        onCancelar={() => onOpenCancelDialog(okr)}
                        onOpenHistorico={() => onOpenHistorico(okr)}
                        onEditOKR={() => onEditOKR(okr)}
                        onAddKR={() => onAddKR(okr.id)}
                        onDeleteOKR={() => onDeleteOKR(okr.id)}
                        onEditKR={(kr) => onEditKR(kr, okr.id)}
                        onDeleteKR={onDeleteKR}
                        onReorderKR={(krId, direction) => onReorderKR(okr.id, krId, direction)}
                        canMoveLeft={statusIdx > 0}
                        canMoveRight={statusIdx < orderedStatusColumns.length - 1}
                        canCancelar={isAdmin}
                        canEditar={isAdmin}
                        allExpanded={allKRsExpanded}
                      />
                    ))}
                    {columnOkrs.length === 0 && (
                      <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                        Nenhuma OKR neste status
                      </div>
                    )}
                  </SortableContext>
                </DroppableColumn>
              </SortableColumn>
            );
          })}
        </div>
      </SortableContext>
      )}
      </>
      )}

      <DragOverlay>
        {activeId && !activeId.startsWith('column-') ? (() => {
          const draggedOkr = okrs.find((o) => o.id === activeId);
          if (!draggedOkr) return null;
          return (
            <div className="opacity-90 rotate-2" style={{ width: '300px' }}>
              <OKRCard
                okr={draggedOkr}
                keyResults={getKRsForOKR(draggedOkr.id)}
                departamento={getDepartamento(draggedOkr.departamento_id)}
                onMoveLeft={() => {}}
                onMoveRight={() => {}}
                onUpdateKR={onUpdateKR}
                onCancelar={() => {}}
                onOpenHistorico={() => {}}
                onEditOKR={() => {}}
                onAddKR={() => {}}
                onDeleteOKR={() => {}}
                onEditKR={() => {}}
                onDeleteKR={() => {}}
                onReorderKR={() => {}}
                canMoveLeft={false}
                canMoveRight={false}
                canCancelar={false}
                canEditar={false}
                isDragging={true}
              />
            </div>
          );
        })() : null}
      </DragOverlay>

      {/* Preview da coluna arrastada: portal manual (fora do DragOverlay do dnd-kit) que
          segue a posição real do ponteiro — ver comentário em handleDragStart. */}
      {activeId && activeId.startsWith('column-') && columnPreviewPos && createPortal(
        (() => {
          const draggedColumn = columnsById.get(columnIdFromDragId(activeId));
          if (!draggedColumn) return null;
          return (
            <div
              className={cn('flex items-center gap-2 p-3 rounded-lg border opacity-90 shadow-lg pointer-events-none fixed top-0 left-0 z-50', draggedColumn.bgClassName)}
              style={{
                width: activeColumnWidth ? `${activeColumnWidth}px` : '280px',
                transform: `translate3d(${columnPreviewPos.x + 12}px, ${columnPreviewPos.y + 12}px, 0)`,
              }}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <draggedColumn.icon className="h-4 w-4" />
              <span className="font-medium text-sm">{draggedColumn.title}</span>
            </div>
          );
        })(),
        document.body
      )}

      {/* Cancelled OKRs Section — mobile only; no desktop vira coluna dentro do grid principal */}
      {cancelledOkrs.length > 0 && (
        <div className="md:hidden space-y-3 mt-6">
          <div className="flex items-center gap-2 p-3 rounded-lg border bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800/50">
            <XCircle className="h-4 w-4 text-destructive" />
            <span className="font-medium text-sm">Canceladas</span>
            <Badge variant="secondary" className="ml-auto bg-red-100 text-red-800">{cancelledOkrs.length}</Badge>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {cancelledOkrs.map((okr) => (
              <CancelledOKRCard key={okr.id} okr={okr} departamento={getDepartamento(okr.departamento_id)} />
            ))}
          </div>
        </div>
      )}
    </DndContext>
  );
}
